<script>
// NOTICE: KAPTION-INTEGRATION
// Widget content i18n translation settings component
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import SettingsSection from '../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import InboxesAPI from '../../../../api/inboxes';

const DEFAULT_TARGET_LOCALES = ['en', 'es', 'pt', 'fr', 'it'];

const AVAILABLE_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'de', name: 'German' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ru', name: 'Russian' },
];

export default {
  name: 'KaptionWebsiteTranslations',
  components: {
    SettingsSection,
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    return { isKaptionFlagEnabled };
  },
  data() {
    return {
      primaryLocale: '',
      targetLocales: [...DEFAULT_TARGET_LOCALES],
      isTranslating: false,
      showPreview: false,
      previewLocale: 'en',
      availableLocales: AVAILABLE_LOCALES,
    };
  },
  computed: {
    isFeatureEnabled() {
      return this.isKaptionFlagEnabled('KAPTION_WIDGET_CUSTOM_I18N');
    },
    kaptionTranslations() {
      return this.inbox.kaption_translations || {};
    },
    translationStatus() {
      return this.kaptionTranslations.status || 'idle';
    },
    translatedAt() {
      if (!this.kaptionTranslations.translated_at) return null;
      return new Date(this.kaptionTranslations.translated_at).toLocaleString();
    },
    hasTranslations() {
      return (
        this.kaptionTranslations.translations &&
        Object.keys(this.kaptionTranslations.translations).length > 0
      );
    },
    statusLabel() {
      const statusMap = {
        idle: this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.IDLE'),
        pending: this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.PENDING'),
        translating: this.$t(
          'INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.TRANSLATING'
        ),
        complete: this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.COMPLETE'),
        failed: this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.FAILED'),
      };
      return statusMap[this.translationStatus] || statusMap.idle;
    },
    statusColor() {
      const colorMap = {
        idle: 'text-n-slate-11',
        pending: 'text-y-600',
        translating: 'text-b-600',
        complete: 'text-g-600',
        failed: 'text-r-600',
      };
      return colorMap[this.translationStatus] || colorMap.idle;
    },
    filteredTargetLocales() {
      // Remove primary locale from target options
      return this.availableLocales.filter(l => l.code !== this.primaryLocale);
    },
    previewTranslations() {
      if (!this.hasTranslations) return {};
      const translations = this.kaptionTranslations.translations;
      return {
        welcome_title:
          translations.welcome_title?.[this.previewLocale] ||
          this.inbox.welcome_title,
        welcome_tagline:
          translations.welcome_tagline?.[this.previewLocale] ||
          this.inbox.welcome_tagline,
        pre_chat_message:
          translations.pre_chat_message?.[this.previewLocale] ||
          this.inbox.pre_chat_form_options?.pre_chat_message,
      };
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler(newInbox) {
        if (newInbox.kaption_translations) {
          this.primaryLocale =
            newInbox.kaption_translations.primary_locale || '';
          this.targetLocales =
            newInbox.kaption_translations.target_locales ||
            DEFAULT_TARGET_LOCALES;
        }
      },
    },
  },
  methods: {
    getLocaleLabel(locale) {
      return `${locale.name} (${locale.code})`;
    },
    getLocaleNameByCode(code) {
      return this.availableLocales.find(l => l.code === code)?.name || code;
    },
    toggleTargetLocale(localeCode) {
      if (this.targetLocales.includes(localeCode)) {
        this.targetLocales = this.targetLocales.filter(l => l !== localeCode);
      } else {
        this.targetLocales = [...this.targetLocales, localeCode];
      }
    },
    async translateContent() {
      if (!this.primaryLocale) {
        useAlert(
          this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.ERRORS.SELECT_PRIMARY')
        );
        return;
      }

      if (this.targetLocales.length === 0) {
        useAlert(
          this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.ERRORS.SELECT_TARGETS')
        );
        return;
      }

      this.isTranslating = true;

      try {
        await InboxesAPI.translateWidgetContent(this.inbox.id, {
          primary_locale: this.primaryLocale,
          target_locales: this.targetLocales,
        });

        useAlert(
          this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.SUCCESS.TRANSLATION_STARTED')
        );

        // Refresh inbox data after a short delay
        setTimeout(() => {
          this.$store.dispatch('inboxes/get');
        }, 2000);
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.KAPTION_TRANSLATIONS.ERRORS.TRANSLATION_FAILED')
        );
      } finally {
        this.isTranslating = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      v-if="isFeatureEnabled"
      :title="$t('INBOX_MGMT.KAPTION_TRANSLATIONS.TITLE')"
      :sub-title="$t('INBOX_MGMT.KAPTION_TRANSLATIONS.DESCRIPTION')"
      :show-border="false"
    >
      <!-- Status Indicator -->
      <div class="flex items-center gap-2 mb-4">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.STATUS.LABEL_WITH_COLON') }}
        </span>
        <span :class="statusColor" class="text-sm font-medium">
          {{ statusLabel }}
        </span>
        <Spinner
          v-if="translationStatus === 'translating' || isTranslating"
          size="small"
        />
        <span v-if="translatedAt" class="text-xs text-n-slate-10">
          {{
            $t('INBOX_MGMT.KAPTION_TRANSLATIONS.TRANSLATED_AT', {
              time: translatedAt,
            })
          }}
        </span>
      </div>

      <!-- Primary Language Selection -->
      <div class="pb-4">
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PRIMARY_LOCALE.LABEL') }}
        </label>
        <select v-model="primaryLocale" class="w-full max-w-xs">
          <option value="">
            {{
              $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PRIMARY_LOCALE.PLACEHOLDER')
            }}
          </option>
          <option
            v-for="locale in availableLocales"
            :key="locale.code"
            :value="locale.code"
          >
            {{ getLocaleLabel(locale) }}
          </option>
        </select>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PRIMARY_LOCALE.HELP') }}
        </p>
      </div>

      <!-- Target Languages Selection -->
      <div class="pb-4">
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.TARGET_LOCALES.LABEL') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <label
            v-for="locale in filteredTargetLocales"
            :key="locale.code"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors"
            :class="
              targetLocales.includes(locale.code)
                ? 'bg-woot-50 border-woot-500 text-woot-700'
                : 'bg-n-alpha-1 border-n-strong hover:bg-n-alpha-2'
            "
          >
            <input
              type="checkbox"
              :checked="targetLocales.includes(locale.code)"
              class="hidden"
              @change="toggleTargetLocale(locale.code)"
            />
            <span class="text-sm">{{ locale.name }}</span>
          </label>
        </div>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.TARGET_LOCALES.HELP') }}
        </p>
      </div>

      <!-- Translate Button -->
      <div class="pb-4">
        <NextButton
          :label="$t('INBOX_MGMT.KAPTION_TRANSLATIONS.TRANSLATE_BUTTON')"
          :is-loading="isTranslating || translationStatus === 'translating'"
          :disabled="!primaryLocale || targetLocales.length === 0"
          @click="translateContent"
        />
      </div>

      <!-- Preview Section -->
      <div v-if="hasTranslations" class="pt-4 border-t border-n-weak">
        <div class="flex items-center justify-between mb-4">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PREVIEW.TITLE') }}
          </h4>
          <select v-model="previewLocale" class="max-w-[150px]">
            <option
              v-for="locale in targetLocales"
              :key="locale"
              :value="locale"
            >
              {{ getLocaleNameByCode(locale) }}
            </option>
          </select>
        </div>

        <div class="space-y-3">
          <div
            v-if="previewTranslations.welcome_title"
            class="p-3 rounded-lg bg-n-alpha-1"
          >
            <span class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PREVIEW.WELCOME_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ previewTranslations.welcome_title }}
            </span>
          </div>

          <div
            v-if="previewTranslations.welcome_tagline"
            class="p-3 rounded-lg bg-n-alpha-1"
          >
            <span class="block text-xs font-medium text-n-slate-10 mb-1">
              {{
                $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PREVIEW.WELCOME_TAGLINE')
              }}
            </span>
            <span
              v-dompurify-html="previewTranslations.welcome_tagline"
              class="text-sm text-n-slate-12"
            />
          </div>

          <div
            v-if="previewTranslations.pre_chat_message"
            class="p-3 rounded-lg bg-n-alpha-1"
          >
            <span class="block text-xs font-medium text-n-slate-10 mb-1">
              {{
                $t('INBOX_MGMT.KAPTION_TRANSLATIONS.PREVIEW.PRE_CHAT_MESSAGE')
              }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ previewTranslations.pre_chat_message }}
            </span>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
